import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import Badge from '@mui/material/Badge';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import { DayCalendarSlotsComponentsProps } from '@mui/x-date-pickers/internals';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchBookings,
  fetchBookingsByMonth,
  selectDate,
} from '../../../redux/booking/bookingSlice';
import {
  fetchBookingsByMonthForMiniweb,
  selectDateInMiniweb,
} from '../../../redux/miniweb/miniwebSlice';

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    top: '80%',
    right: '50%',
    cursor: 'pointer',
  },
}));

const ServerDay = ({
  highlightedDays = [],
  day,
  outsideCurrentMonth,
  ...other
}: PickersDayProps<Dayjs> & { highlightedDays?: number[] }) => {
  const isSelected: boolean = !outsideCurrentMonth && highlightedDays.indexOf(day.date()) >= 0;

  return (
    <StyledBadge
      key={day.toString() || ''}
      overlap="circular"
      badgeContent={
        isSelected ? (
          <img src="/assets/icons/elipes-red.svg" alt="bookings-calendar" style={{ zIndex: -1 }} />
        ) : undefined
      }
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        sx={{
          zIndex: 2,
          '&.Mui-selected': {
            background: 'linear-gradient(175.46deg, #FEE4DB -9.81%, #FFAAA0 96.32%)',
            color: '#000',
            fontWeight: 700,
            zIndex: 1,
          },
        }}
      />
    </StyledBadge>
  );
};

interface ICalendarProps {
  isMiniWeb?: boolean;
  username?: string;
  disabledDates?: Dayjs[];
  onDateChange?: (_date: Dayjs | null) => void;
}

export default function Calendar({
  isMiniWeb = false,
  username,
  disabledDates = [],
  onDateChange,
}: ICalendarProps) {
  const highlightedDays = useAppSelector((state) =>
    isMiniWeb ? [] : state.booking.highlightedDays
  );
  const fetchBookingsByMonthLoading = useAppSelector((state) =>
    isMiniWeb
      ? state.miniweb.fetchBookingsByMonthForMiniwebLoading
      : state.booking.fetchBookingsByMonthLoading
  );
  const selectedDate = useAppSelector((state) =>
    isMiniWeb ? state.miniweb.selectedDateInMiniweb : state.booking.selectedDate
  );

  const {
    i18n: { language },
  } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useAppDispatch();
  const initialValue = dayjs(selectedDate) || dayjs();

  useEffect(() => {
    if (!isMiniWeb) {
      dispatch(
        fetchBookings({
          date: dayjs(selectedDate).date(),
          month: dayjs(selectedDate).month(),
          year: dayjs(selectedDate).year(),
        })
      );
    }
  }, [selectedDate]);

  useEffect(() => {
    if (isMiniWeb && username) {
      dispatch(
        fetchBookingsByMonthForMiniweb({
          username,
          month: dayjs(initialValue).month(),
          year: dayjs(initialValue).year(),
        })
      );
    } else {
      dispatch(
        fetchBookingsByMonth({
          month: dayjs(initialValue).month(),
          year: dayjs(initialValue).year(),
        })
      );
    }
  }, [username]);

  const handleMonthChange = (date: Dayjs) => {
    if (isMiniWeb && username) {
      dispatch(
        fetchBookingsByMonthForMiniweb({
          username,
          month: dayjs(date).month(),
          year: dayjs(date).year(),
        })
      );
    } else {
      dispatch(
        fetchBookingsByMonth({
          month: dayjs(date).month(),
          year: dayjs(date).year(),
        })
      );
    }
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date && date.toISOString() !== selectedDate) {
      if (!isMiniWeb) {
        dispatch(selectDate({ date: date.toISOString() }));
        dispatch(
          fetchBookings({
            date: dayjs(date).date(),
            month: dayjs(date).month(),
            year: dayjs(date).year(),
          })
        );
      } else {
        dispatch(selectDateInMiniweb({ date: date.toISOString() }));
      }
    }
    if (date && new Date(date.toString()).getMonth() !== new Date(selectedDate).getMonth()) {
      if (isMiniWeb && username) {
        dispatch(
          fetchBookingsByMonthForMiniweb({
            username,
            month: dayjs(date).month(),
            year: dayjs(date).year(),
          })
        );
      } else {
        dispatch(
          fetchBookingsByMonth({
            month: dayjs(date).month(),
            year: dayjs(date).year(),
          })
        );
      }
    }
    // Call the onDateChange prop
    if (isMiniWeb && onDateChange) {
      onDateChange(date);
    }
  };

  const today = dayjs();

  const shouldDisableDate = (date: Dayjs) => {
    // Disable past dates
    if (date.isBefore(today, 'day')) {
      return true;
    }

    // Disable custom dates
    if (
      disabledDates.length > 0 &&
      disabledDates.some((disabledDate) => date.isSame(disabledDate, 'day'))
    ) {
      return true;
    }

    return false;
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={language.split('-')[0]}
      key={language}
    >
      <DateCalendar
        sx={{
          justifyContent: 'flex-start',
          border: 'none',
          backgroundColor: '#fff',
          borderRadius: isMiniWeb ? 'none' : '10px',
          boxShadow: isMiniWeb ? 'none' : '0px 4px 40px 0px #FFDDC859',
          maxHeight: '360px !important',
          height: 'auto !important',
          padding: '20px 0 20px 0 !important',
          marginBottom: isSmallScreen ? '20px' : '0',
          width: isSmallScreen ? '300px !important' : '320px',
          '& .MuiDayCalendar-weekDayLabel': {
            color: isMiniWeb ? '#2A3C67' : 'black',
            fontWeight: 700,
          },
          '& .MuiPickersCalendarHeader-root': {
            justifyContent: isMiniWeb ? 'center !important' : 'space-between !important',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: '#7CAEBE',
              transform: 'scaleY(0.5)',
            },
          },
          '& .MuiPickersCalendarHeader-labelContainer': {
            marginRight: isMiniWeb ? '0' : '10px !important',
            display: isMiniWeb ? 'flex' : 'block',
            alignContent: isMiniWeb ? 'center' : 'initial',
            color: isMiniWeb ? '#2A3C67' : 'black',
          },
          '& .MuiPickersCalendarHeader-switchViewButton': {
            display: 'none',
          },
          ...(isMiniWeb && {
            '& .MuiPickersArrowSwitcher-root': {
              position: 'fixed',
            },
          }),
          '& .MuiDateCalendar-root': {
            padding: '20px 0 20px 0 !important',
            backgroundColor: '#000 !important',
            width: isSmallScreen ? '100% !important' : '320px',
          },
          '& .MuiPickersArrowSwitcher-spacer': {
            width: isMiniWeb ? '200px' : '15px',
          },
          '& .MuiPickersArrowSwitcher-button': {
            color: '#6CA3AC !important',
          },
        }}
        defaultValue={isMiniWeb ? null : initialValue}
        loading={fetchBookingsByMonthLoading && disabledDates.length === 0}
        showDaysOutsideCurrentMonth
        dayOfWeekFormatter={(_day, date) => date.format('ddd')}
        onMonthChange={handleMonthChange}
        renderLoading={() => <DayCalendarSkeleton />}
        slots={{
          day: ServerDay,
        }}
        slotProps={{
          day: {
            highlightedDays,
          } as DayCalendarSlotsComponentsProps<Dayjs>,
        }}
        onChange={handleDateChange}
        onYearChange={handleMonthChange}
        shouldDisableDate={isMiniWeb ? shouldDisableDate : undefined}
      />
    </LocalizationProvider>
  );
}
